// src/pages/bonusHistory/hooks/useBonusHistoryScreen.ts
import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getApi } from "../../api/api";
import { BonusStats } from "../../common/types/types";
import { selectIsOnline } from "../../store/slices/networkSlice";
import {
  selectBonusHistory,
  setBonusHistory,
} from "../../store/slices/bonusSlice";

export const useBonusHistoryScreen = () => {
  const dispatch = useDispatch();
  const transactions = useSelector(selectBonusHistory);
  const [stats, setStats] = useState<BonusStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const api = getApi();
  const isOnline = useSelector(selectIsOnline);

  const loadHistory = useCallback(async () => {
    if (!isOnline) return;

    try {
      const response = await api.getBonusHistory(20, 0);
      if (response && Array.isArray(response)) {
        dispatch(setBonusHistory(response));
      }
    } catch (err) {
      console.error("Error loading bonus history:", err);
    }
  }, [isOnline, api, dispatch]);

  const loadStats = useCallback(async () => {
    if (!isOnline) {
      setError("Нет подключения к интернету");
      return;
    }

    try {
      setLoading(true);
      const data = await api.getBonusStats();
      if (data) setStats(data);
      setError(null);
    } catch (err) {
      console.error("Error loading stats:", err);
      setError("Не удалось загрузить статистику");
    } finally {
      setLoading(false);
    }
  }, [isOnline, api]);

  useEffect(() => {
    if (isOnline) {
      loadStats();
      loadHistory();
    }
  }, [isOnline, loadStats, loadHistory]);

  const refresh = useCallback(async () => {
    setError(null);
    await Promise.all([loadStats(), loadHistory()]);
  }, [loadStats, loadHistory]);

  return {
    transactions,
    stats,
    loading,
    error,
    isOnline,
    refresh,
  };
};
